import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Popover,
  Spinner,
  Text,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import {
  useDeleteAuthentication,
  useQueryPrivateImplementationsMeta,
} from "@zapier/client";
import type { ConnectionNeeded } from "@zapier/bff";

import { ConnectAppRow, type RowResolution } from "./ConnectAppRow";
import {
  type ConnectionAppMeta,
  buildConnectionRows,
  groupVersionTag,
  versionless,
} from "./connectionGroups";
import styles from "./ConnectAppsModal.module.css";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  connectionsNeeded: ConnectionNeeded[];
}

/**
 * "Connect your apps" modal. Shown after a cross-account copy: lists each app
 * the copied Zaps reference and lets the user connect an account per app (a new
 * connection via OAuth popup, or an existing one).
 *
 * Deprecated implementations are excluded (they need a version upgrade, not a
 * connection). Remaining apps are grouped by auth-compatibility key (matching
 * zsl-api's `selectedApiCompatKey`), so incompatible variants get their own
 * rows; a version tag disambiguates rows that share an app name. Each row wires
 * independently; the footer dismisses. Closes itself when nothing remains.
 */
export function ConnectAppsModal({
  isOpen,
  setIsOpen,
  connectionsNeeded,
}: Props) {
  const t = useTranslations("connect-apps-modal");
  // How each row was resolved (by row key). A row is "resolved" once the user
  // reconnected its placeholder or wired some auth; unresolved rows are the
  // ones the confirm-on-close warns about.
  const [resolvedByKey, setResolvedByKey] = useState<
    Map<string, RowResolution>
  >(new Map());
  // Confirm-on-close: which footer button (Skip/Done) was pressed while rows
  // are still bare — drives a warning Popover anchored to that button.
  const [confirmTrigger, setConfirmTrigger] = useState<
    "skip" | "done" | undefined
  >();
  const skipReference = useRef<HTMLSpanElement>(null);
  const doneReference = useRef<HTMLSpanElement>(null);
  const { deleteAuthentication } = useDeleteAuthentication();

  const selectedApis = [
    ...new Set(connectionsNeeded.map((c) => versionless(c.selectedApi))),
  ];

  const { implementationsMeta, isLoading } = useQueryPrivateImplementationsMeta(
    {
      selectedApis,
    },
  );

  const metaByApi = useMemo(() => {
    const map = new Map<string, ConnectionAppMeta>();
    for (const meta of implementationsMeta) {
      if (meta.id) map.set(versionless(meta.id), meta as ConnectionAppMeta);
    }
    return map;
  }, [implementationsMeta]);

  // Exclude deprecated implementations — they can't be connected here (the
  // editor requires a version upgrade), so they're dropped rather than wired.
  // Entries with no meta yet (still loading) are kept; we gate rendering on
  // `isLoading` so the filter only takes effect once meta resolves.
  const activeConnectionsNeeded = useMemo(
    () =>
      connectionsNeeded.filter(
        (c) => metaByApi.get(versionless(c.selectedApi))?.isDeprecated !== true,
      ),
    [connectionsNeeded, metaByApi],
  );

  const groups = useMemo(
    () => buildConnectionRows(activeConnectionsNeeded, metaByApi),
    [activeConnectionsNeeded, metaByApi],
  );

  // New copy whenever any row is a pre-created placeholder; otherwise keep the
  // legacy count-aware description.
  const hasPlaceholders = groups.some((g) => g.placeholderAuthId != undefined);

  // Same-named rows appear when one app has auth-incompatible variants (e.g. two
  // Slack majors, each its own compat-key group with the same display name). Tag
  // only the colliding rows with a version label so unique apps stay clean.
  //   groups:  [ SlackCLIAPI@1 "Slack", SlackCLIAPI@2 "Slack", GmailAPI@2 "Gmail" ]
  //   result:  { "SlackCLIAPI@1": "v1", "SlackCLIAPI@2": "v2" }   // Gmail omitted (unique name)
  const disambiguatorByKey = useMemo(() => {
    // Placeholder rows are labelled by their (already unique) title, so only
    // legacy rows need a version tag to disambiguate same-named apps.
    const legacyGroups = groups.filter((g) => g.placeholderAuthId == undefined);
    const nameToKeys = new Map<string, string[]>();
    for (const group of legacyGroups) {
      const name = group.displayMeta?.name || versionless(group.selectedApi);
      nameToKeys.set(name, [...(nameToKeys.get(name) ?? []), group.key]);
    }
    const map = new Map<string, string>();
    for (const keys of nameToKeys.values()) {
      if (keys.length < 2) continue;
      for (const group of legacyGroups) {
        if (keys.includes(group.key))
          map.set(group.key, groupVersionTag(group));
      }
    }
    return map;
  }, [groups]);

  // Placeholder-flow rows the user left bare — neither connected nor switched
  // to an existing auth. Includes both placeholder rows and fallback rows
  // (placeholder failed, but the app still needs connecting). True legacy rows
  // (no placeholder, no covered Zaps) close immediately as before.
  const unresolvedCount = useMemo(
    () =>
      groups.filter(
        (group) =>
          (group.placeholderAuthId != undefined ||
            group.coveredZaps != undefined) &&
          !resolvedByKey.has(group.key),
      ).length,
    [groups, resolvedByKey],
  );

  // Nothing left to connect once deprecated apps are filtered out — dismiss.
  useEffect(() => {
    if (isOpen && !isLoading && groups.length === 0) {
      setIsOpen(false);
    }
  }, [isOpen, isLoading, groups.length, setIsOpen]);

  const handleResolved = (key: string, resolution: RowResolution) => {
    setResolvedByKey((previous) => new Map(previous).set(key, resolution));
  };

  // On close, delete placeholders the user replaced with a DIFFERENT existing
  // auth (orphaned). Placeholders that were reconnected or left untouched are
  // kept so they can be connected later from the App Connections page.
  const deleteOrphanedPlaceholders = () => {
    for (const group of groups) {
      const placeholderAuthId = group.placeholderAuthId;
      if (placeholderAuthId == undefined) continue;
      const resolution = resolvedByKey.get(group.key);
      if (
        resolution &&
        !resolution.reconnectedPlaceholder &&
        resolution.authId !== placeholderAuthId
      ) {
        // Best-effort cleanup: a failure just leaves an orphaned placeholder
        // (still reconnectable from the App Connections page), so don't block
        // the close — but log it so the orphan is traceable instead of silent.
        deleteAuthentication(
          { id: placeholderAuthId },
          {
            onError: (error) => {
              console.error(
                `Failed to delete orphaned placeholder authentication ${placeholderAuthId}`,
                error,
              );
            },
          },
        );
      }
    }
  };

  const finalClose = () => {
    deleteOrphanedPlaceholders();
    setConfirmTrigger(undefined);
    setIsOpen(false);
  };

  // Done / Skip first gate on the confirm when any row is still unresolved,
  // opening the warning Popover anchored to the pressed button.
  const attemptClose = (trigger: "skip" | "done") => {
    if (unresolvedCount > 0) {
      setConfirmTrigger(trigger);
      return;
    }
    finalClose();
  };

  if (!isOpen) return;

  return (
    <ModalDialogRoot
      width={640}
      isOpen={true}
      isDismissable={false}
      onOpenChange={(open) => {
        if (!open) attemptClose("done");
      }}
      aria-label={t("header.title")}
    >
      <ModalDialogHeader textAlign="start">
        <span className={styles.headerStack}>
          {t("header.title")}
          <Text type="Body1" color="TextWeaker" tag="span">
            {hasPlaceholders
              ? t("description-placeholder")
              : t("description", { count: groups.length })}
          </Text>
        </span>
      </ModalDialogHeader>
      <ModalDialogBody textAlign="start">
        <div className={styles.body}>
          {/* Wait for meta so apps render already grouped (no 4→2 flash). */}
          {isLoading ? (
            <div className={styles.loading}>
              <Spinner />
            </div>
          ) : (
            <ul className={styles.list}>
              {groups.map((group) => (
                <li key={group.key} className={styles.listItem}>
                  <ConnectAppRow
                    group={group}
                    isWired={resolvedByKey.has(group.key)}
                    onWired={handleResolved}
                    disambiguator={disambiguatorByKey.get(group.key)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </ModalDialogBody>
      <ModalDialogFooter>
        <span ref={skipReference} className={styles.footerTrigger}>
          <Button
            variant="tertiary"
            type="button"
            onPress={() => attemptClose("skip")}
          >
            {t("buttons.skip")}
          </Button>
        </span>
        <span ref={doneReference} className={styles.footerTrigger}>
          <Button
            variant="primary"
            type="button"
            onPress={() => attemptClose("done")}
          >
            {t("buttons.done")}
          </Button>
        </span>
        <Popover
          color="light"
          title={t("confirm.title", { count: unresolvedCount })}
          placement="top end"
          isCloseHidden={true}
          triggerRef={confirmTrigger === "skip" ? skipReference : doneReference}
          isOpen={confirmTrigger !== undefined}
          onOpenChange={(open) => {
            if (!open) setConfirmTrigger(undefined);
          }}
        >
          <div className={styles.confirmPopover}>
            <Text type="Body3" tag="p" color="TextDefault">
              {t.rich("confirm.message", {
                count: unresolvedCount,
                link: (chunks) => (
                  <Link
                    href="/app/assets/connections"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </Text>
            <div className={styles.confirmActions}>
              <Button
                variant="tertiary"
                size="small"
                type="button"
                onPress={() => setConfirmTrigger(undefined)}
              >
                {t("confirm.go-back")}
              </Button>
              <Button
                variant="primary"
                size="small"
                type="button"
                onPress={finalClose}
              >
                {t("confirm.close-anyway")}
              </Button>
            </div>
          </div>
        </Popover>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}
